import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IResponse } from 'src/app/pages/base/interfaces';
import { MaestroModel } from '../maestro.model';
import { WhatsappTemplateService } from 'src/app/services/notificacion/whatsapp_template.service';


export class WhatsappTemplate extends MaestroModel {

    public static RECORD_NAME = 'whatsapp_templates';
    public static CODIGO_REGISTRO = 'whatsapp_template';
    public static CODIGO_MODULO = 'notificacion';
    public static NOMBRE_SINGULAR = 'Plantilla de Whatsapp';
    public static NOMBRE_PLURAL = 'Plantillas de WhatsApp';

    public static ESTADOS = [
        { valor: 0, nombre: 'Borrador'},
        { valor: 1, nombre: 'Pendiente'},
        { valor: 2, nombre: 'Aprobado'},
        { valor: 3, nombre: 'Rechazado'},
        { valor: 4, nombre: 'Error'},
    ]

    public static ESTADO_BORRADOR = 0;
    public static ESTADO_PENDIENTE = 1;
    public static ESTADO_APROBADO = 2;
    public static ESTADO_RECHAZADO = 3;
    public static ESTADO_ERROR = 4;

    public static normalizarEstado(estado: number): number {
        const estadoNumero = Number(estado);
        const estadosValidos = [
            WhatsappTemplate.ESTADO_BORRADOR,
            WhatsappTemplate.ESTADO_PENDIENTE,
            WhatsappTemplate.ESTADO_APROBADO,
            WhatsappTemplate.ESTADO_RECHAZADO,
            WhatsappTemplate.ESTADO_ERROR,
        ];

        if (estadosValidos.includes(estadoNumero)) {
            return estadoNumero;
        }

        return WhatsappTemplate.ESTADO_PENDIENTE;
    }

    /**
     * Estados de aprobación que la pantalla sabe mostrar.
     *
     * Los cinco primeros son los de siempre. El resto llegan por el webhook
     * `message_template_status_update` y le pegan a plantillas YA APROBADAS,
     * incluso meses después. Antes caían al `pending` de abajo: una plantilla
     * pausada por Meta se leía como "Pendiente", que manda a esperar una
     * aprobación que ya ocurrió.
     */
    private static readonly APPROVE_STATUS_VALIDOS = [
        'draft', 'pending', 'approved', 'rejected', 'error',
        'paused', 'disabled', 'locked', 'flagged', 'limit_exceeded',
        'in_appeal', 'archived', 'deleted', 'pending_deletion', 'unknown',
    ];

    public static normalizarApproveStatus(approveStatus: string): string {
        const normalized = String(approveStatus || '').trim().toLowerCase();

        if (WhatsappTemplate.APPROVE_STATUS_VALIDOS.includes(normalized)) {
            return normalized;
        }

        return 'pending';
    }

    private _idEmpresa:         number;
    private _nombreEmpresa:     string;
    private _fecha:             Date;
    private _mensaje:           string;
    private _estado:            number;
    private _contentSID:        string;
    private _subirImagen:       boolean;
    private _urlImagen:         string;
    private _language:          string;
    private _approveStatus:     string;
    private _approvalId:         string;
    private _rejectReason:     string;
    // === Modelo de componentes de Meta ===
    // Todo esto es opcional. Una plantilla de solo cuerpo con imagen —que es lo
    // único que existía con Twilio— sigue funcionando sin tocar ninguno.
    private _metaTemplateName:  string;
    private _category:          string;
    private _headerFormato:     string;
    private _headerTexto:       string;
    private _footerTexto:       string;
    private _botones:           Record<string, any>[];
    private _flujoDefinicion:   Record<string, any> | null;
    private _flujoNodoInicial:  string | null;


    constructor() {
        super();
        this._idEmpresa = this.seguridad.getCurrentUser()?.id_empresa;
        this._nombreEmpresa = this.seguridad.getCurrentUser()?.nombre_empresa;
        this._estado = WhatsappTemplate.ESTADO_PENDIENTE;
        this._fecha = new Date();
    }

    public set idEmpresa( idEmpresa: number ) { this._idEmpresa = idEmpresa };
    public get idEmpresa(): number { return this._idEmpresa };

    public set nombreEmpresa( nombreEmpresa: string ) { this._nombreEmpresa = nombreEmpresa };
    public get nombreEmpresa(): string { return this._nombreEmpresa };

    public set fecha( fecha: Date ) { this._fecha = fecha };
    public get fecha(): Date { return this._fecha };

    public set mensaje( mensaje: string ) { this._mensaje = mensaje };
    public get mensaje(): string { return this._mensaje };

    public set estado( estado: number ) { this._estado = estado };
    public get estado(): number { return this._estado };

    public set contentSID( contentSID: string ) { this._contentSID = contentSID };
    public get contentSID(): string { return this._contentSID };

    public set subnetImagen( value: boolean ) { this._subirImagen = value };
    public get subnetImagen(): boolean { return this._subirImagen };

    public set urlImagen( value: string ) { this._urlImagen = value };
    public get urlImagen(): string { return this._urlImagen };

    public set language( language: string ) { this._language = language };
    public get language(): string { return this._language || 'es' };

    public set approveStatus( approveStatus: string ) { this._approveStatus = approveStatus };
    public get approveStatus(): string { return this._approveStatus };

    public set approvalId( approvalId: string ) { this._approvalId = approvalId };
    public get approvalId(): string { return this._approvalId };

    public set rejectReason( rejectReason: string ) { this._rejectReason = rejectReason };
    public get rejectReason(): string { return this._rejectReason };

    public set metaTemplateName( value: string ) { this._metaTemplateName = value };
    public get metaTemplateName(): string { return this._metaTemplateName };

    public set category( value: string ) { this._category = value };
    public get category(): string { return this._category || 'UTILITY' };

    public set headerFormato( value: string ) { this._headerFormato = value };
    public get headerFormato(): string { return this._headerFormato || 'NONE' };

    public set headerTexto( value: string ) { this._headerTexto = value };
    public get headerTexto(): string { return this._headerTexto };

    public set footerTexto( value: string ) { this._footerTexto = value };
    public get footerTexto(): string { return this._footerTexto };

    public set botones( value: Record<string, any>[] ) { this._botones = value };
    public get botones(): Record<string, any>[] { return this._botones || [] };

    /**
     * Categorías de plantilla que se ofrecen.
     *
     * `AUTHENTICATION` existe en Meta pero no se expone: son códigos de
     * verificación de un solo uso, y no es una funcionalidad de finexus. Meta
     * nunca recategoriza una plantilla HACIA esa categoría, así que dejarla
     * fuera no rompe nada de lo ya creado.
     */
    public static readonly CATEGORIAS = [
        { label: 'Marketing (promociones, novedades)', value: 'MARKETING' },
        { label: 'Utilidad (avisos, recordatorios, estados de cuenta)', value: 'UTILITY' },
    ];

    /** Categoría de una plantilla nueva. */
    public static readonly CATEGORIA_POR_DEFECTO = 'MARKETING';

    /** Formatos de encabezado. */
    public static readonly FORMATOS_HEADER = [
        { label: 'Sin encabezado', value: 'NONE' },
        { label: 'Texto', value: 'TEXT' },
        { label: 'Imagen', value: 'IMAGE' },
        { label: 'Video', value: 'VIDEO' },
        { label: 'Documento', value: 'DOCUMENT' },
    ];

    /** Tipos de botón. */
    public static readonly TIPOS_BOTON = [
        { label: 'Respuesta rápida', value: 'QUICK_REPLY' },
        { label: 'Abrir enlace', value: 'URL' },
        { label: 'Llamar por teléfono', value: 'PHONE_NUMBER' },
    ];

    /**
     * Límites que Meta documenta.
     *
     * Se replican acá para poder avisar en el formulario antes de enviar: la
     * aprobación de Meta no tiene plazo comprometido, así que un rechazo cuesta
     * días. El backend los valida igual; esto es solo para dar feedback temprano.
     */
    public static readonly LIMITES = {
        BODY: 1024,
        HEADER_TEXTO: 60,
        FOOTER: 60,
        BOTONES: 10,
        BOTON_ETIQUETA: 25,
        BOTON_URL: 2000,
    };

    public static get columsOnLista(): Record<string, any>[] {
        return [
            { field: 'fecha', label: 'Fecha', type: 'date' },
            { field: 'nombre', label: 'Nombre', type: 'text' },
            { field: 'mensaje', label: 'Mensaje', type: 'text' },
            { field: 'estado', label: 'Estado', type: 'multiselect', options: WhatsappTemplate.ESTADOS },
        ]
    }

    async cargarDatos( objeto: Record<string, any> ) {
        this._idEmpresa = objeto.id_empresa;
        this._nombreEmpresa = objeto.nombre_empresa;
        this._fecha = objeto.fecha ? new Date(objeto.fecha) : objeto.fecha;
        this._mensaje = objeto.mensaje;
        this._estado = WhatsappTemplate.normalizarEstado(objeto.estado);
        this._contentSID = objeto.content_sid;
        this._subirImagen = objeto.subir_imagen;
        this._urlImagen = objeto.url_imagen;
        this._language = objeto.language || 'es';
        this._approveStatus = WhatsappTemplate.normalizarApproveStatus(objeto.approve_status);
        this._approvalId = objeto.approval_id;
        this._rejectReason = objeto.reject_reason;
        this._metaTemplateName = objeto.meta_template_name;
        // Una plantilla nueva arranca en MARKETING. Una YA GUARDADA sin categoría
        // es de la era Twilio y se queda en UTILITY, que es lo que el backend
        // asumía: subirla a MARKETING encarecería cada mensaje sin que nadie lo pida.
        this._category = objeto.category
            || (objeto.id ? 'UTILITY' : WhatsappTemplate.CATEGORIA_POR_DEFECTO);
        // Compatibilidad con las plantillas de la era Twilio: no tienen
        // header_format, pero sí subir_imagen. Se deriva para que la pantalla
        // muestre lo mismo que antes.
        this._headerFormato = objeto.header_format || (objeto.subir_imagen ? 'IMAGE' : 'NONE');
        this._headerTexto = objeto.header_text;
        this._footerTexto = objeto.footer_text;
        this._botones = Array.isArray(objeto.buttons) ? objeto.buttons : [];
        this._flujoDefinicion = objeto.flujo_definicion || null;
        this._flujoNodoInicial = objeto.flujo_nodo_inicial || null;
    }

    async cargarObjeto( objeto: Record<string, any> ) {
        await super.cargarObjeto( objeto );
        await this.cargarDatos( objeto );
        
    }

    async actualizarDatos( objeto: Record< string, any> ) {
        await super.actualizarDatos( objeto );
        await this.cargarDatos( objeto );
    }

    /**
     * Retorna el formulario a utilizar en las ventanas
     * @returns FormGroup
     */
    async getFormGroup(): Promise<FormGroup> {
        let fg = await super.getFormGroup();
        fg.addControl("id_empresa", new FormControl(this._idEmpresa, Validators.required));
        fg.addControl("nombre_empresa", new FormControl(this._nombreEmpresa, Validators.required));
        fg.addControl("fecha", new FormControl(this._fecha, Validators.required));
        fg.addControl("mensaje", new FormControl(this._mensaje, Validators.required));
        fg.addControl("estado", new FormControl(this._estado, Validators.required));
        fg.addControl("content_sid", new FormControl(this._contentSID));
        fg.addControl("subir_imagen", new FormControl(this._subirImagen));
        fg.addControl("url_imagen", new FormControl(this._urlImagen));
        fg.addControl("language", new FormControl(this._language));
        fg.addControl("approve_status", new FormControl(this._approveStatus));
        fg.addControl("approval_id", new FormControl(this._approvalId));
        fg.addControl("reject_reason", new FormControl(this._rejectReason));

        // === Modelo de componentes de Meta ===
        // Lo asigna Meta al crear la plantilla; acá es solo de lectura.
        fg.addControl("meta_template_name", new FormControl({ value: this._metaTemplateName, disabled: true }));
        // UTILITY por defecto: es lo que el backend ya asumía, así que quien no
        // toque el campo obtiene el comportamiento de siempre.
        fg.addControl("category", new FormControl(this._category || WhatsappTemplate.CATEGORIA_POR_DEFECTO, Validators.required));
        fg.addControl("header_format", new FormControl(this._headerFormato || 'NONE'));
        fg.addControl("header_text", new FormControl(this._headerTexto));
        fg.addControl("footer_text", new FormControl(this._footerTexto));
        fg.addControl("buttons", new FormControl(this._botones || []));
        fg.addControl("flujo_definicion", new FormControl(this._flujoDefinicion));
        fg.addControl("flujo_nodo_inicial", new FormControl(this._flujoNodoInicial));
        return fg;
    }

    async guardar( service: WhatsappTemplateService ): Promise<IResponse> {
        return await service.post( this.serverJson );
    }

    async actualizar( service: WhatsappTemplateService  ): Promise<IResponse> {
        return await service.put(this.id, this.serverJson);
    }

    async eliminar( service: WhatsappTemplateService ): Promise<IResponse> {
        return await service.delete(this.id);
    }

}
